import { useCallback, useEffect, useMemo, useState } from 'react';

import { BULK_DEVICE_PAGE_SIZE } from '../constants/pagination.js';
import { useDeviceLinks } from './useDeviceLinks.js';
import { useDevices } from './useDevices.js';

export function useLinkedDevices(
  deviceId: string,
  options?: { onActiveDeviceCleared?: () => void },
) {
  const { onActiveDeviceCleared } = options ?? {};

  const { data: deviceLinks = [] } = useDeviceLinks(deviceId);
  const { data: devicesPage } = useDevices({ pageSize: BULK_DEVICE_PAGE_SIZE });
  const devices = useMemo(() => devicesPage?.devices ?? [], [devicesPage]);

  const [activeLinkedDeviceId, setActiveLinkedDeviceId] = useState<string | null>(null);

  const linkedDeviceIds = useMemo(
    () => new Set(deviceLinks.map((link) => link.linkedDeviceId)),
    [deviceLinks],
  );

  const linkedDevices = useMemo(
    () =>
      devices
        .filter((device) => linkedDeviceIds.has(device.id))
        .sort(
          (a, b) =>
            b.activeDetectionGroupsCount - a.activeDetectionGroupsCount ||
            a.serial.localeCompare(b.serial),
        ),
    [devices, linkedDeviceIds],
  );

  const activeLinkedDevice = useMemo(
    () => linkedDevices.find((device) => device.id === activeLinkedDeviceId) ?? null,
    [linkedDevices, activeLinkedDeviceId],
  );

  const toggleLinkedDevice = useCallback((linkedDeviceId: string) => {
    setActiveLinkedDeviceId((prev) => (prev === linkedDeviceId ? null : linkedDeviceId));
  }, []);

  // Reset activeLinkedDeviceId when deviceId changes
  useEffect(() => {
    setActiveLinkedDeviceId(null);
  }, [deviceId]);

  // Active-device-gone: fires only on the gone-transition, atomically resets id + calls callback
  useEffect(() => {
    if (
      activeLinkedDeviceId &&
      !linkedDevices.some((device) => device.id === activeLinkedDeviceId)
    ) {
      setActiveLinkedDeviceId(null);
      onActiveDeviceCleared?.();
    }
  }, [activeLinkedDeviceId, linkedDevices, onActiveDeviceCleared]);

  return {
    linkedDevices,
    activeLinkedDeviceId,
    activeLinkedDevice,
    toggleLinkedDevice,
  };
}
